
"use client"
import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Trash2, Image as ImageIcon, Plus, Check, X, ArrowUp, ArrowDown, Info, Download, Save } from 'lucide-react';
import { SituationSetupData } from '@/app/types';

interface QuizEditorProps {
  onBack: () => void;
}

interface Answer {
  id: string;
  text: string;
  isCorrect: boolean;
  image?: string;
}

interface Question {
  id: string;
  text: string;
  image?: string;
  answers: Answer[];
}

export const QuizEditor: React.FC<QuizEditorProps> = ({ onBack }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<{ qId: string, aId?: string } | null>(null);

  // State for Title and Instructions to include in Export/Save
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const raw = window.sessionStorage.getItem('situation-setup');
    if (!raw) return;

    try {
      const setup = JSON.parse(raw) as SituationSetupData;
      if (setup.title) setTitle(setup.title);
      if (setup.instructions) setInstructions(setup.instructions);
    } catch (error) {
      console.error('Failed to parse setup data:', error);
    }
  }, []);

  const [questions, setQuestions] = useState<Question[]>([
    {
      id: '1',
      text: '',
      answers: [
        { id: 'a', text: '', isCorrect: true },
        { id: 'b', text: '', isCorrect: false },
        { id: 'c', text: '', isCorrect: false },
        { id: 'd', text: '', isCorrect: true },
      ]
    },
    {
      id: '2',
      text: '',
      answers: [
        { id: 'a', text: '', isCorrect: false },
        { id: 'b', text: '', isCorrect: false },
        { id: 'c', text: '', isCorrect: false },
        { id: 'd', text: '', isCorrect: true },
      ]
    }
  ]);

  const handleAddQuestion = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setQuestions([...questions, {
      id: newId,
      text: '',
      answers: [
        { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false },
        { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false },
        { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false },
        { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false },
      ]
    }]);
  };

  const handleAddAnswer = (qId: string) => {
    setQuestions(questions.map(q => {
      if (q.id !== qId) return q;
      return {
        ...q,
        answers: [
          ...q.answers,
          { id: Math.random().toString(36).substr(2, 9), text: '', isCorrect: false }
        ]
      };
    }));
  };

  const handleDeleteQuestion = (id: string) => {
    if (questions.length > 1) {
      setQuestions(questions.filter(q => q.id !== id));
    }
  };

  const handleDeleteAnswer = (qId: string, aId: string) => {
    setQuestions(questions.map(q => {
      if (q.id !== qId) return q;
      if (q.answers.length <= 1) return q; // Prevent deleting last answer
      return {
        ...q,
        answers: q.answers.filter(a => a.id !== aId)
      };
    }));
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === questions.length - 1)
    ) return;

    const newQuestions = [...questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    setQuestions(newQuestions);
  };

  const updateQuestionText = (id: string, text: string) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, text } : q));
  };

  const updateAnswer = (qId: string, aId: string, field: 'text' | 'isCorrect', value: any) => {
    setQuestions(questions.map(q => {
      if (q.id !== qId) return q;
      return {
        ...q,
        answers: q.answers.map(a => a.id === aId ? { ...a, [field]: value } : a)
      };
    }));
  };

  const handleImageUploadClick = (qId: string, aId?: string) => {
    setUploadTarget({ qId, aId });
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;

    const imageUrl = URL.createObjectURL(file);

    setQuestions(questions.map(q => {
      if (q.id !== uploadTarget.qId) return q;

      if (uploadTarget.aId) {
        return {
          ...q,
          answers: q.answers.map(a => a.id === uploadTarget.aId ? { ...a, image: imageUrl } : a)
        };
      } else {
        return { ...q, image: imageUrl };
      }
    }));

    e.target.value = ''; // Reset input
    setUploadTarget(null);
  };

  const getLabel = (index: number) => {
    return String.fromCharCode(65 + index); // Returns A, B, C, D...
  };

  // --- Export JSON Logic ---
  const handleExportJSON = () => {
    const dataToExport = {
      title: title || "Untitled Activity",
      instructions: instructions || "",
      questions: questions,
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${(title || 'activity').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // --- Save to DB Logic ---
  const handleSave = async () => {
    const payload = {
      title,
      instructions,
      questions,
      createdAt: new Date().toISOString()
    };

    // NOTE: This is where you would make the API call to your backend (Next.js API route/Express/etc)
    // which then connects to PostgreSQL.
    /*
    try {
      const response = await fetch('/api/save-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        alert('Saved successfully!');
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
    */

    // Simulating success for UI demo
    console.log("Saving to DB:", payload);
    alert(`Saved "${title || 'Untitled'}" to database! (Check console for payload)`);
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-white font-sans pb-32 rounded-2xl">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/*"
      />

      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header Inputs */}
        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activity Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fff264] focus:border-transparent outline-none"
              placeholder="Enter activity title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Instructions
              <Info className="w-4 h-4 text-gray-400" />
            </label>
            <input
              type="text"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fff264] focus:border-transparent outline-none text-gray-400"
              placeholder="Enter instructions"
            />
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-6">
          {questions.map((q, qIndex) => (
            <div key={q.id} className="flex gap-4 items-start transition-all duration-300 ease-in-out">
              <span className="mt-4 font-medium text-gray-500 w-6 text-right">{qIndex + 1}.</span>

              <div className="flex-1 bg-gray-100/80 rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                {/* Card Header */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-600">Question</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveQuestion(qIndex, 'down')}
                      disabled={qIndex === questions.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-opacity"
                    >
                      <ArrowDown className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleMoveQuestion(qIndex, 'up')}
                      disabled={qIndex === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-opacity"
                    >
                      <ArrowUp className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(q.id)}
                      className="p-1 text-red-400 hover:text-red-600 ml-2 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Question Input */}
                <div className="relative mb-6">
                  <input
                    type="text"
                    value={q.text}
                    onChange={(e) => updateQuestionText(q.id, e.target.value)}
                    className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fff264] focus:border-transparent outline-none bg-white transition-shadow"
                    placeholder="Type your question here..."
                  />
                  <button
                    onClick={() => handleImageUploadClick(q.id)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#009fe3] hover:text-[#007bb0] transition-colors"
                    title="Upload image"
                  >
                    {q.image ? (
                      <div className="w-8 h-8 rounded overflow-hidden border border-gray-200">
                        <img src={q.image} alt="Question" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <ImageIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Answers Label */}
                <div className="mb-2 text-sm font-medium text-gray-600">Answers</div>

                {/* Answers Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {q.answers.map((answer, aIndex) => (
                    <div key={answer.id} className="flex items-center gap-2 group">
                      {/* Label & Toggle Group */}
                      <div className="flex border border-gray-300 rounded-md overflow-hidden shrink-0 bg-white shadow-sm">
                        <div className="w-8 h-10 flex items-center justify-center bg-gray-50 border-r border-gray-200 text-sm font-medium text-gray-600">
                          {getLabel(aIndex)}
                        </div>
                        <button
                          onClick={() => updateAnswer(q.id, answer.id, 'isCorrect', !answer.isCorrect)}
                          className={`w-10 h-10 flex items-center justify-center transition-colors ${answer.isCorrect ? 'bg-white' : 'bg-white'
                            }`}
                          title={answer.isCorrect ? "Mark as incorrect" : "Mark as correct"}
                        >
                          {answer.isCorrect ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <X className="w-5 h-5 text-red-500" />
                          )}
                        </button>
                      </div>

                      {/* Input Field */}
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={answer.text}
                          onChange={(e) => updateAnswer(q.id, answer.id, 'text', e.target.value)}
                          className="w-full pl-3 pr-9 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#fff264] focus:border-transparent outline-none bg-white h-10 transition-shadow"
                          placeholder={`Answer ${getLabel(aIndex)}`}
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <button
                            onClick={() => handleImageUploadClick(q.id, answer.id)}
                            className="text-[#009fe3] hover:text-[#007bb0] p-1 rounded transition-colors"
                            title="Upload image"
                          >
                            {answer.image ? (
                              <div className="w-6 h-6 rounded overflow-hidden border border-gray-200">
                                <img src={answer.image} alt="Answer" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <ImageIcon className="w-4 h-4" />
                            )}
                          </button>
                          {/* Delete Answer Button (only show on hover and if enough answers) */}
                          {q.answers.length > 2 && (
                            <button
                              onClick={() => handleDeleteAnswer(q.id, answer.id)}
                              className="text-gray-300 hover:text-red-500 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete answer"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Answer Button */}
                <button
                  onClick={() => handleAddAnswer(q.id)}
                  className="w-full mt-4 py-2 flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200/50 rounded transition-colors text-sm font-medium border border-dashed border-gray-300 hover:border-gray-400"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add more answers</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add Question Button */}
        <div className="mt-8">
          <button
            onClick={handleAddQuestion}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors flex items-center gap-2 text-sm shadow-sm border border-gray-300"
          >
            <Plus className="w-4 h-4" />
            <span>Add question</span>
          </button>
        </div>

      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t border-gray-200 flex justify-end gap-3 z-20">
        <button
          onClick={handleExportJSON}
          className="px-6 py-2.5 bg-white text-[#009fe3] border-2 border-[#009fe3] rounded-lg font-bold text-sm hover:bg-sky-50 transition-all shadow-sm flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          export json
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-2.5 bg-[#009fe3] text-white rounded-lg font-bold text-sm hover:bg-[#008bc7] transition-all shadow-md flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
        {/* Original Done button (kept as requested in some flows, but usually Save replaces it) */}
        <button
          onClick={onBack} // Assuming 'Done' also exits for now, or just does nothing
          className="px-6 py-2.5 bg-[#fff264] text-gray-900 rounded-lg font-bold text-sm hover:bg-[#faeb5a] transition-all shadow-md"
        >
          Done
        </button>
      </div>

      {/* Back Button */}
      <button
        onClick={onBack}
        className="fixed bottom-6 left-6 px-6 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-50 shadow-sm z-30"
      >
        Back
      </button>

    </div>
  );
};

export default QuizEditor;